import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomerService } from '../../../../../../../../../services/customer/customer.service';
import { CustomerMembership } from '../../../../../../../../../models/membership';

/**
 * Cancel a membership that was paid for but never activated, because it has
 * been sitting awaiting a payment method (PRO-1460).
 *
 * Nothing cancels these automatically, so this is how staff clear one out. The
 * only decision is where the money goes, which is why this is a much smaller
 * modal than the immediate-cancellation one: that membership actually ran, so
 * it needs a cancellation email, a refund policy and possibly a reason. This
 * one never started.
 */
@Component({
  selector: 'app-cancel-unactivated-customer-membership',
  templateUrl: './cancel-unactivated-customer-membership.component.html',
  styleUrls: ['./cancel-unactivated-customer-membership.component.css']
})
export class CancelUnactivatedCustomerMembershipComponent implements OnInit, OnDestroy {

  @Input() membership: CustomerMembership;
  @Input() savedCallback: (successMessage: string) => undefined;
  @Input() saveClickedObservable: Observable<void>;
  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() serverErrorEvent = new EventEmitter<number>();

  public refundToSource = true;

  public errorMessage = '';

  private saveClickedSubscription: Subscription;

  constructor(
    private activeModal: NgbActiveModal,
    private customerService: CustomerService
  ) {}

  ngOnInit() {
    this.saveClickedSubscription = this.saveClickedObservable.subscribe(() => {
      this.handleSave();
    });
  }

  ngOnDestroy() {
    this.saveClickedSubscription.unsubscribe();
  }

  private handleSave() {
    this.loadingEvent.emit(true);

    this.customerService
      .cancelUnactivatedMembership(this.membership, this.refundToSource)
      .then(() => {
        this.savedCallback('Membership cancelled successfully.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to cancel this membership.';
        } else if (reason.status === 400) {
          // This endpoint returns a bare string, matching the other membership
          // cancellation endpoints this modal sits alongside.
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => { this.loadingEvent.emit(false); });
  }

}
